import React, { useState } from 'react';
import {
  ApplicationIcon,
  Button,
  Icon,
  Menu,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import SodRadialGauge from '../../../../../shared/components/SodRadialGauge/SodRadialGauge';
import {
  type BarChartData,
  StackedBarChart,
} from '../../../../../shared/components/StackedBarChart';
import { useInvertedLocalize } from '../../../../../shared/hooks';
import {
  applicationLogoMap,
  APPLICATIONS_WITH_MOST_WASTE_ITEMS,
  categoryCostCenterChartData,
  categoryRegionChartData,
  departmentChartData,
  INDUSTRY_BENCHMARKS_ITEMS,
  LICENSES_WITH_MOST_UNASSIGNED_SEATS_ITEMS,
  segmentColors,
  segmentGradients,
  SOFTWARE_OBSOLESCENCE_ITEMS,
  SOFTWARE_OBSOLESCENCE_METRIC,
  USERS_WITH_MOST_WASTE_ITEMS,
  vendorChartData,
  VENDORS_WITH_MOST_DORMANT_USERS_ITEMS,
} from '../../../mock-data/graphicalViewMockData';

import {
  CATEGORY_MENU_OPTIONS,
  CATEGORY_OPTIONS,
  CHART_HEIGHT,
  CHART_WIDTH,
  DEFAULT_CATEGORY_OPTION,
  DEFAULT_GROUP_BY_OPTION,
  DEFAULT_METRIC_OPTION,
  DEFAULT_TIME_PERIOD_OPTION,
  GAUGE_CONFIG,
  GAUGE_LEGEND_ITEMS,
  GRAPHICAL_VIEW_SYMBOLS,
  GROUP_BY_OPTIONS,
  METRIC_MENU_OPTIONS,
  OPTIMIZATION_WIDGETS,
  OVERLAY_METRIC_CONFIG,
  STATS_CONFIG,
  TIME_PERIOD_MENU_OPTIONS,
  TOGGLE_BUTTONS,
} from './constants';

import './GraphicalView.css';

/** Renders the application icon for a chart bar's x-axis label. */
function XAxisAppIcon({ item }: { item: BarChartData }): React.ReactElement {
  const logo = applicationLogoMap[item.label] || item.label;

  return <ApplicationIcon logo={logo} size='small' />;
}

const iconXAxisFormatter = (
  _index: number,
  item: BarChartData
): React.ReactElement => <XAxisAppIcon item={item} />;

const textXAxisFormatter = (_index: number, item: BarChartData): string =>
  item.label;

/**
 * GraphicalView component for License Intelligence Dashboard
 * @description Displays a comprehensive graphical analysis of license spend and usage with interactive filtering.
 * Includes stacked bar charts grouped by vendor/department/category, a radial gauge showing compliance metrics,
 * and optimization widgets for software obsolescence and industry benchmarks. Provides filtering by group-by option,
 * category, metric, and time period with real-time chart updates.
 * @returns {React.ReactElement} Rendered graphical view with charts, gauges, and optimization widgets
 */
function GraphicalView(): React.ReactElement {
  const localize = useInvertedLocalize();
  const [groupByOption, setGroupByOption] = useState(DEFAULT_GROUP_BY_OPTION);
  const [categoryOption, setCategoryOption] = useState(DEFAULT_CATEGORY_OPTION);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [metricOption, setMetricOption] = useState(DEFAULT_METRIC_OPTION);
  const [isMetricMenuExpanded, setIsMetricMenuExpanded] = useState(false);
  const [timePeriodOption, setTimePeriodOption] = useState(
    DEFAULT_TIME_PERIOD_OPTION
  );
  const [isTimePeriodMenuExpanded, setIsTimePeriodMenuExpanded] =
    useState(false);

  let chartData = vendorChartData;

  if (groupByOption === GROUP_BY_OPTIONS.DEPARTMENT) {
    chartData = departmentChartData;
  } else if (groupByOption === GROUP_BY_OPTIONS.CATEGORY) {
    chartData =
      categoryOption === CATEGORY_OPTIONS.REGION
        ? categoryRegionChartData
        : categoryCostCenterChartData;
  }

  const xAxisConfig =
    groupByOption === GROUP_BY_OPTIONS.DEPARTMENT ||
    groupByOption === GROUP_BY_OPTIONS.CATEGORY
      ? { type: 'text' as const, formatter: textXAxisFormatter }
      : { type: 'icon' as const, formatter: iconXAxisFormatter };

  const timePeriodLabel =
    TIME_PERIOD_MENU_OPTIONS.find((o) => o.value === timePeriodOption)?.label ??
    timePeriodOption;

  return (
    <div className='GraphicalView'>
      {/* Main License Spend/Usage Card */}
      <div className='GraphicalView-license-spend'>
        <div className='GraphicalView-header'>
          <div className='GraphicalView-titleSection'>
            <Typography kind='h2'>{localize('License Spend/Usage')}</Typography>
          </div>

          <div className='GraphicalView-toggleGroup'>
            {TOGGLE_BUTTONS.map((button) => (
              <Button
                key={button.value}
                className={cx('GraphicalView-toggleBtn', button.className, {
                  'GraphicalView-toggleBtn--active':
                    groupByOption === button.value,
                })}
                onClick={() => setGroupByOption(button.value)}>
                {button.label}
              </Button>
            ))}
            <Menu
              options={CATEGORY_MENU_OPTIONS}
              trigger={
                <Button
                  className={cx('GraphicalView-categoryMenuBtn', {
                    'GraphicalView-categoryMenuBtn--active':
                      groupByOption === GROUP_BY_OPTIONS.CATEGORY,
                  })}>
                  {localize('Category')}
                  <Icon kind='ChevronDown' size='small' />
                </Button>
              }
              isMenuExpanded={isMenuExpanded}
              setIsExpanded={setIsMenuExpanded}
              onChange={(option: MenuOption) => {
                setGroupByOption(GROUP_BY_OPTIONS.CATEGORY);
                setCategoryOption(
                  option.value === CATEGORY_OPTIONS.COST_CENTER
                    ? CATEGORY_OPTIONS.COST_CENTER
                    : CATEGORY_OPTIONS.REGION
                );
              }}
              shouldCloseMenuOnItemClick
              isOutsideClickEnabled
              size='small'
              placement='bottom-start'
              classNames={{
                dropdownClassName: 'GraphicalView-categoryMenuDropdown',
              }}
            />
          </div>
          <div className='GraphicalView-filterActions'>
            <Menu
              options={METRIC_MENU_OPTIONS}
              trigger={
                <Button className='GraphicalView-filterMenuBtn'>
                  {metricOption === 'annual-cost'
                    ? 'By Annual Cost'
                    : 'By Utilization'}
                  <Icon kind='ChevronDown' size='small' />
                </Button>
              }
              isMenuExpanded={isMetricMenuExpanded}
              setIsExpanded={setIsMetricMenuExpanded}
              onChange={(option: MenuOption) => {
                setMetricOption(option.value);
              }}
              shouldCloseMenuOnItemClick
              isOutsideClickEnabled
              size='small'
              placement='bottom-start'
              classNames={{
                dropdownClassName: 'GraphicalView-filterMenuDropdown',
              }}
            />
            <Menu
              options={TIME_PERIOD_MENU_OPTIONS}
              trigger={
                <Button className='GraphicalView-filterMenuBtn'>
                  {timePeriodLabel}
                  <Icon kind='ChevronDown' size='small' />
                </Button>
              }
              isMenuExpanded={isTimePeriodMenuExpanded}
              setIsExpanded={setIsTimePeriodMenuExpanded}
              onChange={(option: MenuOption) => {
                setTimePeriodOption(option.value);
              }}
              shouldCloseMenuOnItemClick
              isOutsideClickEnabled
              size='small'
              placement='bottom-start'
              classNames={{
                dropdownClassName: 'GraphicalView-filterMenuDropdown',
              }}
            />
          </div>
        </div>

        <div className='GraphicalView-content'>
          {/* Left side - Chart and Stats */}
          <div className='GraphicalView-chartSection'>
            {/* Stats */}
            <div className='GraphicalView-stats'>
              {STATS_CONFIG[groupByOption as keyof typeof STATS_CONFIG].map(
                (stat, index, array) => (
                  <React.Fragment key={stat.label}>
                    <div className='GraphicalView-statItem'>
                      <div className='GraphicalView-statValue'>
                        {stat.value}
                        <div
                          className={cx('GraphicalView-statTrend', {
                            'GraphicalView-statTrend--down':
                              stat.trendType === 'down',
                            'GraphicalView-statTrend--success':
                              stat.trendType !== 'down',
                          })}>
                          <span className='GraphicalView-trendValue'>
                            {stat.trend}
                          </span>
                          <Icon
                            kind={
                              stat.trendType === 'up' ? 'TrendUp' : 'TrendDown'
                            }
                            size='small'
                            className='GraphicalView-trendIcon'
                          />
                        </div>
                      </div>
                      <div className='GraphicalView-statLabel'>
                        {stat.label}
                      </div>
                    </div>
                    {index < array.length - 1 && (
                      <div className='GraphicalView-divider' />
                    )}
                  </React.Fragment>
                )
              )}
            </div>

            {/* Chart */}
            <div className='GraphicalView-chart'>
              <StackedBarChart
                data={chartData}
                width={CHART_WIDTH}
                height={CHART_HEIGHT}
                showLegend
                segmentColors={segmentColors}
                segmentGradients={segmentGradients}
                xAxisConfig={xAxisConfig}
                overlayMetric={OVERLAY_METRIC_CONFIG}
              />
            </div>
          </div>

          <div className='gauge-divider' />

          {/* Right side - Radial Gauge and Widgets */}
          <div className='GraphicalView-sidePanel'>
            {/* Radial Gauge */}
            <div className='GraphicalView-gaugeContainer'>
              <div className='GraphicalView-gauge'>
                <SodRadialGauge
                  value={GAUGE_CONFIG.value}
                  total={GAUGE_CONFIG.total}
                  rotation={GAUGE_CONFIG.rotation}
                  label={GAUGE_CONFIG.label}
                />
              </div>

              {/* Gauge Legend */}
              <div className='GraphicalView-gaugeLegend'>
                {GAUGE_LEGEND_ITEMS.map((item) => (
                  <div key={item.logo} className='GraphicalView-legendItem'>
                    <span className='GraphicalView-legendValue'>
                      {item.value}
                    </span>
                    <ApplicationIcon logo={item.logo} size='xSmall' />
                  </div>
                ))}
              </div>
            </div>

            {/* Optimization Widgets */}
            <div className='GraphicalView-widgets'>
              {OPTIMIZATION_WIDGETS.map((widget) => (
                <div key={widget.text} className='GraphicalView-widget'>
                  <p className='GraphicalView-widgetText'>{widget.text}</p>
                  <Icon kind='ChevronRight' size='small' />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Widgets Row */}
      <div className='GraphicalView-widgetsRow'>
        {/* Software Obsolescence Widget */}
        <div className='GraphicalView-widget-card GraphicalView-widget-card--dark'>
          <div className='GraphicalView-widget-header'>
            <Typography
              kind='h3'
              className='GraphicalView-widget-title--primary'>
              {localize('Software Obsolescence')}
            </Typography>
            <button type='button' className='GraphicalView-widget-menu'>
              <Icon kind='MoreVertical' size='small' />
            </button>
          </div>

          <div className='GraphicalView-widget-content'>
            <div className='GraphicalView-widget-metric'>
              <Typography kind='display4-bold'>
                {SOFTWARE_OBSOLESCENCE_METRIC.value}
              </Typography>
              <Typography kind='body1'>
                {localize(SOFTWARE_OBSOLESCENCE_METRIC.label)}
              </Typography>
            </div>

            <div className='GraphicalView-widget-toggle'>
              <Button className='GraphicalView-widget-toggleBtn GraphicalView-widget-toggleBtn--active'>
                {localize('Expired')}
              </Button>
              <Button className='GraphicalView-widget-toggleBtn'>
                {localize('Upcoming')}
              </Button>
            </div>

            <div className='GraphicalView-widget-list'>
              {SOFTWARE_OBSOLESCENCE_ITEMS.map((item) => (
                <div
                  key={item.appName}
                  className='GraphicalView-widget-listItem'>
                  <div className='GraphicalView-widget-itemLeft'>
                    <div className='GraphicalView-widget-appInfo'>
                      <ApplicationIcon logo={item.logo} size='xSmall' />
                      <Typography kind='body2'>{item.appName}</Typography>
                    </div>
                    <Typography
                      kind='body3'
                      className='GraphicalView-widget-expiryDate'>
                      {item.expiryDate}
                    </Typography>
                  </div>
                  <div className='GraphicalView-widget-itemRight'>
                    <Typography kind='body3'>
                      {localize('Cost')}
                      {GRAPHICAL_VIEW_SYMBOLS.COLON} {item.cost}
                    </Typography>
                    <Typography kind='body3'>
                      {localize('Usage')}
                      {GRAPHICAL_VIEW_SYMBOLS.COLON} {item.usage}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>

            <div className='GraphicalView-widget-footer'>
              <Button className='GraphicalView-widget-viewAll'>
                {localize('View All')}
                <Icon
                  kind='ChevronRight'
                  size='small'
                  className='GraphicalView-chevronIcon'
                />
              </Button>
            </div>
          </div>
        </div>

        {/* Industry Benchmarks Widget */}
        <div className='GraphicalView-widget-card'>
          <div className='GraphicalView-widget-header'>
            <Typography
              kind='h3'
              className='GraphicalView-widget-title--primary'>
              {localize('Industry Benchmarks')}
            </Typography>
            <button type='button' className='GraphicalView-widget-menu'>
              <Icon kind='MoreVertical' size='small' />
            </button>
          </div>

          <div className='GraphicalView-widget-benchmarkList'>
            {INDUSTRY_BENCHMARKS_ITEMS.map((item) => (
              <div key={item.label} className='GraphicalView-benchmark-item'>
                <div className='GraphicalView-benchmark-content'>
                  <div className='GraphicalView-benchmark-colorKey' />
                  <div className='GraphicalView-benchmark-info'>
                    <Typography kind='body1'>{localize(item.label)}</Typography>
                    <Typography
                      kind='body3'
                      className='GraphicalView-benchmark-sublabel'>
                      {localize(item.sublabel)}
                    </Typography>
                  </div>
                </div>
                <div
                  className={cx(
                    'GraphicalView-benchmark-chip',
                    `GraphicalView-benchmark-chip--${item.chipType}`
                  )}>
                  <Typography kind='body3'>{item.percentage}</Typography>
                </div>
              </div>
            ))}
          </div>

          <div className='GraphicalView-widget-footer'>
            <Button className='GraphicalView-widget-viewAll'>
              {localize('View All')}
              <Icon
                kind='ChevronRight'
                size='small'
                className='GraphicalView-chevronIcon'
              />
            </Button>
          </div>
        </div>

        {/* Applications With Most License Waste Widget */}
        <div className='GraphicalView-widget-card'>
          <div className='GraphicalView-widget-header'>
            <Typography
              kind='h3'
              className='GraphicalView-widget-title--primary'>
              {localize('Applications With Most License Waste')}
            </Typography>
            <button type='button' className='GraphicalView-widget-menu'>
              <Icon kind='MoreVertical' size='small' />
            </button>
          </div>

          <div className='GraphicalView-widget-appsList'>
            {APPLICATIONS_WITH_MOST_WASTE_ITEMS.map((app) => (
              <div key={app.name} className='GraphicalView-widget-appItem'>
                <div className='GraphicalView-widget-appInfo'>
                  <ApplicationIcon logo={app.logo} size='small' />
                  <Typography kind='body1'>{app.name}</Typography>
                </div>
                <Typography kind='body2'>{app.waste}</Typography>
              </div>
            ))}
          </div>

          <div className='GraphicalView-widget-footer'>
            <Button className='GraphicalView-widget-viewAll'>
              {localize('View All')}
              <Icon
                kind='ChevronRight'
                size='small'
                className='GraphicalView-chevronIcon'
              />
            </Button>
          </div>
        </div>

        {/* Users With Most License Waste Widget */}
        <div className='GraphicalView-widget-card'>
          <div className='GraphicalView-widget-header'>
            <Typography
              kind='h3'
              className='GraphicalView-widget-title--primary'>
              {localize('Users With Most License Waste')}
            </Typography>
            <button type='button' className='GraphicalView-widget-menu'>
              <Icon kind='MoreVertical' size='small' />
            </button>
          </div>

          <div className='GraphicalView-widget-usersList'>
            {USERS_WITH_MOST_WASTE_ITEMS.map((user) => (
              <div key={user.name} className='GraphicalView-widget-userItem'>
                <div className='GraphicalView-widget-userInfo'>
                  <div className='GraphicalView-widget-avatar' />
                  <Typography kind='body1'>{user.name}</Typography>
                </div>
                <Typography kind='body2'>{user.waste}</Typography>
              </div>
            ))}
          </div>

          <div className='GraphicalView-widget-footer'>
            <Button className='GraphicalView-widget-viewAll'>
              {localize('View All')}
              <Icon
                kind='ChevronRight'
                size='small'
                className='GraphicalView-chevronIcon'
              />
            </Button>
          </div>
        </div>

        {/* Vendors With Most Dormant Users Widget */}
        <div className='GraphicalView-widget-card'>
          <div className='GraphicalView-widget-header'>
            <Typography
              kind='h3'
              className='GraphicalView-widget-title--primary'>
              {localize('Vendors With Most Dormant Users')}
            </Typography>
            <button type='button' className='GraphicalView-widget-menu'>
              <Icon kind='MoreVertical' size='small' />
            </button>
          </div>

          <div className='GraphicalView-widget-vendorsList'>
            {VENDORS_WITH_MOST_DORMANT_USERS_ITEMS.map((vendor) => (
              <div
                key={vendor.name}
                className='GraphicalView-widget-vendorItem'>
                <div className='GraphicalView-widget-vendorInfo'>
                  <ApplicationIcon logo={vendor.logo} size='xSmall' />
                  <Typography kind='body1'>{vendor.name}</Typography>
                </div>
                <Typography kind='body2'>{vendor.count}</Typography>
              </div>
            ))}
          </div>

          <div className='GraphicalView-widget-footer'>
            <Button className='GraphicalView-widget-viewAll'>
              {localize('View All')}
              <Icon
                kind='ChevronRight'
                size='small'
                className='GraphicalView-chevronIcon'
              />
            </Button>
          </div>
        </div>

        {/* Licenses with Most Unassigned Seats Widget */}
        <div className='GraphicalView-widget-card'>
          <div className='GraphicalView-widget-header'>
            <Typography
              kind='h3'
              className='GraphicalView-widget-title--primary'>
              {localize('Licenses with Most Unassigned Seats')}
            </Typography>
            <button type='button' className='GraphicalView-widget-menu'>
              <Icon kind='MoreVertical' size='small' />
            </button>
          </div>

          <div className='GraphicalView-widget-licensesList'>
            {LICENSES_WITH_MOST_UNASSIGNED_SEATS_ITEMS.map((license) => (
              <div
                key={license.name}
                className='GraphicalView-widget-licenseItem'>
                <div className='GraphicalView-widget-licenseInfo'>
                  <ApplicationIcon logo={license.logo} size='xSmall' />
                  <Typography kind='body1'>{license.name}</Typography>
                </div>
                <Typography kind='body2'>{license.seats}</Typography>
              </div>
            ))}
          </div>

          <div className='GraphicalView-widget-footer'>
            <Button className='GraphicalView-widget-viewAll'>
              {localize('View All')}
              <Icon
                kind='ChevronRight'
                size='small'
                className='GraphicalView-chevronIcon'
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GraphicalView;
